import React, { useState, useEffect } from 'react';
import { User, Phone, Mail, MapPin, CreditCard, Bell, Shield, LogOut, ChevronRight, Loader2, X, Home, Building } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getUserProfile, signOut } from './services/authService';
import { supabase } from './services/supabase';

const AccountPageStyles = () => (
  <style>{`
    .ap-page {
      max-width: 640px;
      margin: 0 auto;
      padding: 40px 20px;
      min-height: calc(100vh - 140px);
    }

    /* Header */
    .ap-header { text-align: center; margin-bottom: 28px; }
    .ap-avatar {
      width: 80px; height: 80px; border-radius: 50%;
      background: #2F7D5E; display: flex; align-items: center; justify-content: center;
      margin: 0 auto 14px; color: white;
    }
    .ap-name { font-size: 1.3rem; font-weight: 400; color: #1e293b; margin-bottom: 4px; }
    .ap-email { font-size: 0.9rem; color: #94a3b8; }

    /* Stats */
    .ap-stats {
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px;
      margin-bottom: 24px;
    }
    .ap-stat {
      background: white; border: 1px solid #e5e7eb; border-radius: 4px;
      padding: 16px; text-align: center;
    }
    .ap-stat-value { font-size: 1.3rem; font-weight: 400; color: #1e293b; margin-bottom: 4px; }
    .ap-stat-label { font-size: 0.75rem; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.3px; }

    /* Section */
    .ap-section {
      background: white; border: 1px solid #e5e7eb; border-radius: 4px;
      padding: 18px; margin-bottom: 14px;
    }
    .ap-section-title {
      font-size: 1rem; font-weight: 400; color: #2F7D5E;
      letter-spacing: 0.3px; margin-bottom: 14px; padding-bottom: 10px;
      border-bottom: 1px solid #1e293b;
      display: flex; align-items: center; gap: 8px;
    }

    /* Menu item */
    .ap-menu-item {
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px; background: none; border: none; width: 100%;
      cursor: default; border-radius: 4px; text-align: left; gap: 12px;
      transition: background 0.15s;
    }
    .ap-menu-item.clickable { cursor: pointer; }
    .ap-menu-item.clickable:hover { background: #f8fafc; }
    .ap-menu-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; }
    .ap-menu-icon {
      width: 38px; height: 38px; border-radius: 4px;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; background: #f1f5f9; color: #1e293b;
    }
    .ap-menu-content { flex: 1; min-width: 0; }
    .ap-menu-label { font-size: 0.9rem; font-weight: 400; color: #1e293b; margin-bottom: 2px; }
    .ap-menu-value { font-size: 0.8rem; color: #94a3b8; }
    .ap-menu-chevron { color: #cbd5e1; flex-shrink: 0; }

    /* Logout */
    .ap-logout {
      display: flex; align-items: center; justify-content: center; gap: 8px;
      width: 100%; padding: 14px; background: white; color: #ef4444;
      border: 1px solid #e5e7eb; border-radius: 4px;
      font-size: 0.9rem; font-weight: 400; cursor: pointer; transition: all 0.2s;
      margin-top: 24px;
    }
    .ap-logout:hover { border-color: #fca5a5; background: #fef2f2; }

    /* Address modal */
    .ap-modal-overlay {
      position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000;
      display: flex; align-items: center; justify-content: center;
      animation: ap-fadeIn 0.2s ease;
    }
    .ap-modal {
      background: white; width: 100%; max-width: 500px; max-height: 80vh;
      border-radius: 4px; padding: 24px; overflow-y: auto;
      margin: 20px; animation: ap-slideUp 0.25s ease;
    }
    .ap-modal-header {
      display: flex; justify-content: space-between; align-items: center;
      margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid #1e293b;
    }
    .ap-modal-title { font-size: 1rem; font-weight: 400; color: #2F7D5E; }
    .ap-modal-close {
      background: #f1f5f9; border: none; border-radius: 50%;
      width: 32px; height: 32px; display: flex; align-items: center;
      justify-content: center; cursor: pointer; color: #64748b;
    }
    .ap-modal-close:hover { background: #e2e8f0; }

    .ap-addr-list { display: flex; flex-direction: column; gap: 12px; }
    .ap-addr-card {
      border: 1px solid #e5e7eb; border-radius: 4px; padding: 14px;
    }
    .ap-addr-tag {
      display: inline-flex; align-items: center; gap: 4px;
      background: #f1f5f9; padding: 3px 10px; border-radius: 4px;
      font-size: 0.8rem; font-weight: 400; color: #64748b; margin-bottom: 8px;
    }
    .ap-addr-main { font-size: 0.95rem; font-weight: 400; color: #1e293b; margin-bottom: 3px; }
    .ap-addr-sub { font-size: 0.85rem; color: #64748b; }
    .ap-addr-empty { text-align: center; padding: 40px 0; color: #94a3b8; }

    /* Loading */
    .ap-loading {
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; min-height: calc(100vh - 140px); gap: 12px;
      color: #94a3b8;
    }
    .ap-spinner { animation: ap-spin 1s linear infinite; }
    @keyframes ap-spin { to { transform: rotate(360deg); } }
    @keyframes ap-fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes ap-slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }

    @media (max-width: 640px) {
      .ap-page { padding: 24px 16px; }
      .ap-stats { grid-template-columns: 1fr 1fr 1fr; gap: 8px; }
      .ap-stat { padding: 12px; }
      .ap-modal { margin: 16px; border-radius: 4px; }
    }

    @media (max-width: 375px) {
      .ap-modal-overlay { align-items: flex-end; }
      .ap-modal { margin: 0; border-radius: 4px 4px 0 0; max-height: 85vh; }
    }
  `}</style>
);

const AccountPage = ({ currentUser, userProfile: initialProfile, onProfileUpdate }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!initialProfile);
  const [userProfile, setUserProfile] = useState(initialProfile);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  useEffect(() => {
    if (!currentUser) { navigate('/'); return; }
    if (initialProfile) { setUserProfile(initialProfile); setLoading(false); return; }
    loadUserData();
  }, [currentUser, initialProfile, navigate]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const profileResult = await getUserProfile(currentUser.id);
      if (profileResult.success) {
        setUserProfile(profileResult.data);
        onProfileUpdate && onProfileUpdate(profileResult.data);
      }
    } catch (error) {
      // silently ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedAddresses = async () => {
    if (!currentUser) return;
    setLoadingAddresses(true);
    setShowAddressModal(true);
    try {
      const { data, error } = await supabase
        .from('saved_addresses')
        .select('*')
        .eq('user_id', currentUser.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSavedAddresses(data || []);
    } catch (err) {
      // silently ignore
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleLogout = async () => {
    if (window.confirm('確定要登出嗎？')) {
      const result = await signOut();
      if (result.success) {
        navigate('/');
        setTimeout(() => { window.location.reload(); }, 100);
      } else {
        alert('登出失敗，請稍後再試');
      }
    }
  };

  if (loading) {
    return (
      <>
        <AccountPageStyles />
        <div className="ap-loading">
          <Loader2 size={32} className="ap-spinner" />
          <p>載入中...</p>
        </div>
      </>
    );
  }

  if (!userProfile) {
    return (
      <>
        <AccountPageStyles />
        <div className="ap-loading">
          <p>無法載入用戶資料</p>
          <button onClick={() => navigate('/')}
            style={{ padding: '10px 24px', background: '#1e293b', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 400 }}>
            返回首頁
          </button>
        </div>
      </>
    );
  }

  const menuItems = [
    { icon: Phone, label: '手機號碼', value: userProfile.phone_number || '未設定', action: null },
    { icon: Mail, label: '電子郵件', value: userProfile.email || '未設定', action: null },
  ];

  const settingItems = [
    { icon: CreditCard, label: '付款方式', value: '管理信用卡' },
    { icon: Bell, label: '通知設定', value: '推送通知' },
    { icon: Shield, label: '隱私與安全', value: '帳號設定' },
  ];

  return (
    <>
      <AccountPageStyles />
      <div className="ap-page">
        <div className="ap-header">
          <div className="ap-avatar"><User size={36} /></div>
          <h1 className="ap-name">{userProfile.name || '用戶'}</h1>
          <p className="ap-email">{userProfile.email || userProfile.phone_number}</p>
        </div>

        <div className="ap-section">
          <div className="ap-section-title">個人資訊</div>
          {menuItems.map((item, index) => (
            <button
              key={index}
              className={`ap-menu-item ${item.action ? 'clickable' : ''}`}
              onClick={item.action || undefined}
            >
              <div className="ap-menu-left">
                <div className="ap-menu-icon"><item.icon size={18} /></div>
                <div className="ap-menu-content">
                  <div className="ap-menu-label">{item.label}</div>
                  <div className="ap-menu-value">{item.value}</div>
                </div>
              </div>
              {item.action && <ChevronRight size={18} className="ap-menu-chevron" />}
            </button>
          ))}
        </div>

        <button className="ap-logout" onClick={handleLogout}>
          <LogOut size={18} /> 登出
        </button>
      </div>

      {showAddressModal && (
        <div className="ap-modal-overlay" onClick={() => setShowAddressModal(false)}>
          <div className="ap-modal" onClick={e => e.stopPropagation()}>
            <div className="ap-modal-header">
              <div className="ap-modal-title">我的地址簿</div>
              <button className="ap-modal-close" onClick={() => setShowAddressModal(false)}>
                <X size={16} />
              </button>
            </div>

            {loadingAddresses ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}>
                <Loader2 size={28} className="ap-spinner" />
              </div>
            ) : savedAddresses.length > 0 ? (
              <div className="ap-addr-list">
                {savedAddresses.map(addr => (
                  <div key={addr.id} className="ap-addr-card">
                    <div className="ap-addr-tag">
                      {addr.tag === '家' ? <Home size={12} /> : <Building size={12} />}
                      {addr.tag}
                    </div>
                    <div className="ap-addr-main">
                      {addr.address} {addr.unit && `#${addr.unit}`}
                    </div>
                    <div className="ap-addr-sub">
                      {addr.city}, {addr.province} {addr.postal}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="ap-addr-empty">
                <MapPin size={40} style={{ opacity: 0.2, marginBottom: 10 }} />
                <p>目前沒有儲存的地址</p>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AccountPage;
